import React, { FunctionComponent, useEffect, useState } from 'react';
import { IGetCustomers } from 'utils/models';
import { getCustomersState } from 'utils/states';
import WhiteCard from 'global/atoms/WhiteCard';
import Pagination from 'global/pagination/Pagination';
import Filters from 'global/filters/Filters';
import CustomersConnection from 'utils/connections/customers';
import { deepClone, getAddressFullName } from 'utils/helpers';
import Input from 'global/atoms/Input';
import List from 'global/table/List';
import { Cell, Column, HeaderCell } from 'rsuite-table';
import arrow from 'assets/svg/arrow_down_small.svg';
import settings from 'assets/svg/settings.svg';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import HeaderButtons from 'global/atoms/headerButtons/HeaderButtons';
import ButtonAddNew from 'global/atoms/ButtonAddNew';
import ButtonFilters from 'global/atoms/ButtonFilters';
import Spinner from 'global/atoms/Spinner/Spinner';
import { handleClearFilters, loadFilters } from '../../../../utils/filterHelper';
import FiltersStorage from '../../../../utils/FiltersStorage';

interface ICustomersList {}

const CustomersList: FunctionComponent<ICustomersList> = () => {
  const [data, setData] = useState<any | 'null' | 'error'>(null);
  const defaultFilters = deepClone(getCustomersState);
  const [filters, setFilters] = useState<IGetCustomers>(loadFilters(getCustomersState));
  const [loading, setLoading] = useState(true);
  const [toggleFilters, setToggleFilters] = useState<boolean>(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const [form, setForm] = useState<any>({});

  useEffect(() => {
    CustomersConnection.getListForm().then((response) => {
      setForm(response.data);
    });
  }, []);

  const reloadData = () => {
    CustomersConnection.getCustomers(filters).then((data) => {
      setLoading(false);
      setData(data.data);
    });
  };

  useEffect(() => {
    setFilters((state: IGetCustomers) => ({
      ...state,
      requestPaginate: {
        page: 1,
        limit: state.requestPaginate.limit,
      },
    }));

    CustomersConnection.getCustomers(filters).then((data) => {
      setLoading(false);
      setData(data.data);
      data.data?.header && dispatch({ type: 'SET_HEADER', payload: data.data.header });
    });
  }, [filters.requestPaginate.limit]);

  useEffect(() => {
    CustomersConnection.getCustomers(filters).then((data) => {
      setLoading(false);
      setData(data.data);
      data.data?.header && dispatch({ type: 'SET_HEADER', payload: data.data.header });
    });
  }, [filters.requestPaginate.page, filters.requestOrder]);

  const handleToggleSort = (event: any) => {
      if (filters.requestOrder.field === event.currentTarget.dataset.field) {
        setFilters({
          ...filters,
          requestOrder: {
            field: filters.requestOrder.field,
            order: filters.requestOrder.order === 'asc' ? 'desc' : 'asc',
          },
        });
      } else {
        setFilters({
          ...filters,
          requestOrder: {
            field: event.currentTarget.dataset.field,
            order: 'asc',
          },
        });
      }
    },
    handleToggleFilters = () => setToggleFilters((state: boolean) => !state),
    handleSubmitFilters = (event: any) => {
      event.preventDefault();

      const filtersStorage = new FiltersStorage();
      filtersStorage.save(filters);

      CustomersConnection.getCustomers({
        ...filters,
        requestPaginate: {
          page: 1,
          limit: filters.requestPaginate.limit,
        },
      }).then((data) => {
        setLoading(false);
        setToggleFilters(false);
        setData(data.data);
        data.data?.header && dispatch({ type: 'SET_HEADER', payload: data.data.header });
        setFilters((state: IGetCustomers) => ({
          ...state,
          requestPaginate: { page: 1, limit: state.requestPaginate.limit },
        }));
      });
    },
    handleEdit = (event: any) => {
      history.push(`/admin/customers/${event.currentTarget.dataset.id}/edit`);
    },
    handleRowClick = (data: any) => {
      history.push(`/admin/customers/${data.id}/edit`);
    };

  if (loading) return <Spinner />;

  if (data === null) return null;

  if (data === 'error') return <div>Błąd pobierania danych</div>;

  return (
    <>
      <HeaderButtons>
        <ButtonFilters onClick={handleToggleFilters} />
        <ButtonAddNew form={form} />
      </HeaderButtons>
      <WhiteCard padding={false} mobileTransparent={true}>
        <List data={data.data} count={data.data.length} rowClick={handleRowClick}>
          <Column flexGrow={1} minWidth={200} align="left">
            <HeaderCell depth={0} width={0}>
              <span
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                onClick={handleToggleSort}
                data-field={'name'}
              >
                Nazwa
                {filters.requestOrder.field === 'name' &&
                  (filters.requestOrder.order === 'asc' ? (
                    <img src={arrow} alt={'sort'} />
                  ) : (
                    <img src={arrow} alt={'sort'} style={{ transform: 'rotate(180deg)' }} />
                  ))}
              </span>
            </HeaderCell>
            <Cell width={100} depth={0} dataKey="name" />
          </Column>
          <Column flexGrow={1} minWidth={300} align="left">
            <HeaderCell depth={0} width={0}>
              Adres
            </HeaderCell>
            <Cell width={0} depth={0} dataKey="name">
              {(rowData) => {
                return <span>{getAddressFullName(rowData.address)}</span>;
              }}
            </Cell>
          </Column>
          <Column width={200} align="left">
            <HeaderCell depth={0} width={0}>
              Typ
            </HeaderCell>
            <Cell depth={0} width={0}>
              {(rowData) => <span>{rowData.customerType.name}</span>}
            </Cell>
          </Column>
          <Column width={200} align="left">
            <HeaderCell depth={0} width={0}>
              Email
            </HeaderCell>
            <Cell depth={0} width={0}>
              {(rowData) => <span>{rowData.email}</span>}
            </Cell>
          </Column>
          <Column verticalAlign={'middle'} align="center" fixed="right">
            <HeaderCell depth={0} width={0}>
              Akcje
            </HeaderCell>
            <Cell depth={0} width={0}>
              {(rowData) => (
                <img
                  src={settings}
                  style={{ cursor: 'pointer' }}
                  alt={'Idź do'}
                  data-id={rowData.id}
                  onClick={handleEdit}
                />
              )}
            </Cell>
          </Column>
        </List>
      </WhiteCard>
      <Pagination
        count={data.count}
        limit={filters.requestPaginate.limit.toString()}
        page={filters.requestPaginate.page.toString()}
        setState={setFilters}
      />

      <Filters
        toggleFilters={toggleFilters}
        handleToggleFilters={handleToggleFilters}
        handleSubmit={handleSubmitFilters}
        handleClear={handleClearFilters.bind(null, setFilters, defaultFilters, reloadData)}
      >
        <Input
          value={filters.name}
          placeholder={'Nazwa'}
          name={'name'}
          type={'text'}
          setState={setFilters}
          state={filters}
        />
      </Filters>
    </>
  );
};

export default CustomersList;
